import {
  CarbonUnit,
  convert,
  ConvertUnits,
  MeasurementSystem,
} from "common/utils/units/units";
export * from "common/utils/units/units";
import { BestResult, Measure } from "convert-units";
import { keys } from "../objects";
import area, { AreaUnits } from "convert-units/definitions/area";
import coverage, { CoverageUnits } from "common/utils/units/coverage";
import density, { DensityUnits } from "common/utils/units/density";
import speed, { SpeedUnits } from "convert-units/definitions/speed";
import time, { TimeUnits } from "convert-units/definitions/time";

// denylist units that are never appropriate in scale for our usecases
const EXCLUDED_UNITS: ConvertUnits[] = [
  // AREA
  "nm2",
  "μm2",
  "mm2",
  "cm2",
  "yd2",
  "in2",
  // LENGTH
  "nm",
  "μm",
  "mil",
  "yd",
  "ft-us",
  "fathom",
  "nMi",
  // SPEED
  "mm/h",
  "knot",
  "m/s",
  "ft/min",
  "in/h",
  // POWER
  "mW",
  "kW",
  "MW",
  "GW",
  "PS",
  "Btu/s",
  "ft-lb/s",
  "hp",
  // TIME
  "ns",
  "mu",
  "ms",
];
const EXCLUDED_UNITS_SET = new Set(EXCLUDED_UNITS);

/**
 * @returns the units in the given measurement system that fromUnit can be converted to
 **/
export const getUnitsForSystem = (
  fromUnit: ConvertUnits,
  toSystem: MeasurementSystem
): ConvertUnits[] => {
  const cycle = convert().from(fromUnit).possibilities();
  const fromSystem = convert().describe(fromUnit).system;
  if (fromSystem === MeasurementSystem.si) {
    toSystem = MeasurementSystem.si;
  }

  return cycle.filter(
    (unit) =>
      !EXCLUDED_UNITS_SET.has(unit) &&
      convert().describe(unit).system === toSystem
  );
};

type AnchoredBestResult = BestResult & { to_anchor: number };

export const getCarbonBest = (
  value: number,
  fromUnits: ConvertUnits,
  toSystem: MeasurementSystem
): BestResult | null => {
  const system =
    convert().describe(fromUnits).system === MeasurementSystem.si
      ? undefined
      : toSystem;
  // try to get best units
  let converted = convert(value)
    .from(fromUnits)
    .toBest({
      system,
      exclude: EXCLUDED_UNITS,
      cutOffNumber: value < 0 ? -1 : 1,
    });
  // handle -> -1 < x < 1
  // see https://github.com/convert-units/convert-units/issues/346
  if (!converted) {
    const conversionOptions: AnchoredBestResult[] = [];
    const possibilities = convert()
      .from(fromUnits)
      .possibilities()
      .filter((p) => !EXCLUDED_UNITS_SET.has(p));
    for (const possibility of possibilities) {
      const unit = convert().describe(possibility);
      const unitDetails = convert().getUnit(possibility);
      if (!unitDetails) {
        continue;
      }
      if (unit.system === system) {
        const result = convert(value).from(fromUnits).to(possibility);
        conversionOptions.push({
          val: result,
          unit: possibility,
          singular: unit.singular,
          plural: unit.plural,
          to_anchor: unitDetails.unit.to_anchor,
        });
      }
    }
    // pick the largest from the options -- if there are absolutely no options then return null
    if (conversionOptions.length > 0) {
      // eslint-disable-next-line unicorn/no-null -- null matches the convert library output
      let best: AnchoredBestResult | null = null;
      for (const option of conversionOptions) {
        if (best === null || option.to_anchor < best.to_anchor) {
          best = option;
        }
      }
      converted = best;
    }
  }
  return converted;
};

export const resultToTArguments = (
  best: BestResult,
  longUnits: boolean = false
): { i18nKey: string; options?: any } => {
  const result: { i18nKey: string; options?: any } = {
    // carbon.actions.compareKeys.ignoreDynamic
    i18nKey: `utils.units.${best.unit}`,
  };
  if (longUnits) {
    result.i18nKey += "Long";
    result.options = { count: best.val };
  }
  return result;
};

export const allUnits = (measure: Measure<any, any>): string[] => [
  ...keys(measure.systems.imperial),
  ...keys(measure.systems.metric),
];

export const isSpeed = (units: CarbonUnit): units is SpeedUnits =>
  allUnits(speed).includes(units);

export const isDensity = (units: CarbonUnit): units is DensityUnits =>
  allUnits(density).includes(units);

export const isCoverage = (units: CarbonUnit): units is CoverageUnits =>
  allUnits(coverage).includes(units);

export const isArea = (units: CarbonUnit): units is AreaUnits =>
  allUnits(area).includes(units);

export const isTime = (units: CarbonUnit): units is TimeUnits =>
  allUnits(time).includes(units);

// getCarbonBest() will convert speeds to a mixture of units depending on the
// scale of the value's magnitude.  this can lead to a confusing mixture of
// m/s and km/h or ft/s and mph if the values fall into certain ranges.  prefer
// using a specific speed unit for vehicle speeds, the one customarily
// displayed on local vehicle speedometers.
export const preferredVehicleSpeedUnit: {
  [k in MeasurementSystem]: SpeedUnits;
} = {
  [MeasurementSystem.imperial]: "mph",
  [MeasurementSystem.metric]: "km/h",
  [MeasurementSystem.si]: "km/h",
};
